import React from 'react'
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Typography, Box, Chip, Avatar, IconButton, Tooltip, Link
} from '@mui/material'
import { IconSeeker, FlagsIconSeeker } from '@ui-seeker/core'

// Calculate if a price is an outlier for its market
const isPriceOutlier = (price, market, priceAlerts) => {
    if (!price || !market) return false
    const alert = priceAlerts.find(a => a.market === market)
    if (!alert) return false

    // Consider outlier if price differs significantly from min/max
    const midPrice = (alert.min_price + alert.max_price) / 2
    const threshold = midPrice * 0.3 // 30% from middle

    return price < (alert.min_price + threshold * 0.1) || price > (alert.max_price - threshold * 0.1)
}

// Check if price is at min or max for its market
const getPricePosition = (price, market, priceAlerts) => {
    if (!price || !market) return null
    const alert = priceAlerts.find(a => a.market === market)
    if (!alert || alert.sites_count <= 1) return null

    if (Math.abs(price - alert.min_price) < 0.01) return 'min'
    if (Math.abs(price - alert.max_price) < 0.01) return 'max'
    return null
}

export default function MappingsTable({ mappings, priceAlerts }) {
    if (!mappings || mappings.length === 0) {
        return (
            <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No mappings found for the selected date range
                </Typography>
            </Paper>
        )
    }

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ bgcolor: 'grey.100' }}>Site</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100' }}>Product</TableCell>
                        <TableCell align="right" sx={{ bgcolor: 'grey.100' }}>Price</TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'grey.100' }}>Stock</TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'grey.100' }}>Last Seen</TableCell>
                        <TableCell align="center" sx={{ bgcolor: 'grey.100' }}>Link</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {mappings.map((mapping, index) => {
                        const pricePosition = getPricePosition(mapping.price, mapping.market, priceAlerts)
                        const isOutlier = pricePosition === 'max' && priceAlerts.find(a => a.market === mapping.market)?.alert_level !== 'ok'

                        return (
                            <TableRow
                                key={`${mapping.id_site}-${mapping.product_id}`}
                                sx={{
                                    bgcolor: isOutlier ? 'error.lighter' : 'inherit',
                                    '&:hover': { bgcolor: isOutlier ? 'error.light' : 'action.hover' }
                                }}
                            >
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FlagsIconSeeker countryName={mapping.market} />
                                        <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 140 }}>
                                            {mapping.site}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {mapping.image_url && (
                                            <Avatar
                                                src={mapping.image_url}
                                                variant="rounded"
                                                sx={{ width: 32, height: 32 }}
                                            />
                                        )}
                                        <Box>
                                            <Tooltip title={mapping.product_name || ''}>
                                                <Typography
                                                    variant="body2"
                                                    noWrap
                                                    sx={{ maxWidth: 200 }}
                                                >
                                                    {mapping.product_name || mapping.product_id}
                                                </Typography>
                                            </Tooltip>
                                            {mapping.subtitle && (
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                                                    {mapping.subtitle}
                                                </Typography>
                                            )}
                                            {mapping.variant_raw && (
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                                                    {mapping.variant_raw}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    {mapping.price != null ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                            {pricePosition === 'min' && (
                                                <Tooltip title="Lowest price in market">
                                                    <Chip label="MIN" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                                                </Tooltip>
                                            )}
                                            {pricePosition === 'max' && (
                                                <Tooltip title="Highest price in market">
                                                    <Chip label="MAX" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
                                                </Tooltip>
                                            )}
                                            <Typography
                                                variant="body2"
                                                fontWeight="medium"
                                                color={pricePosition === 'max' ? 'error.main' : pricePosition === 'min' ? 'success.main' : 'inherit'}
                                            >
                                                {mapping.currency}{mapping.price.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">-</Typography>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <Tooltip title={mapping.has_stock ? 'In Stock' : 'No Stock'}>
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                bgcolor: mapping.has_stock ? 'success.main' : 'error.main',
                                                display: 'inline-block'
                                            }}
                                        />
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {mapping.last_seen || '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    {mapping.product_url && (
                                        <Tooltip title="Open product page">
                                            <IconButton
                                                size="small"
                                                component={Link}
                                                href={mapping.product_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <IconSeeker icon="external-link" size="sm" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
